/**
 * Notifications with a title and a body, rendered in the shared ToastCard.
 *
 * `toast.success('8 de 10 dispositivos sincronizados (3 local, 5 público)')` is one sentence
 * doing three jobs — naming the event, counting it and qualifying it — and at toast width it
 * wraps into a paragraph with nothing to anchor the eye. Split in two, the same information
 * reads at a glance: "Sincronização parcial" / "8 de 10 dispositivos (3 local, 5 público)".
 *
 * Plain `toast.*` calls still work: App renders them in the same card with a title derived
 * from the toast type. This is for callers that can say something better than "Falha".
 */

import React from 'react';
import toast from 'react-hot-toast';
import { ToastCard, ToastTone } from '../components/ui/ToastCard';

/** A failure has to survive the glance that a success does not need. */
const DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 4000,
  // A warning and a failure both need the extra beat a success does not.
  warning: 7000,
  error: 7000,
  loading: Infinity,
};

export interface NotifyOptions {
  /** Reuse an id to update a toast in place — how a progress toast counts up. */
  id?: string;
  duration?: number;
}

function show(tone: ToastTone, title: React.ReactNode, body?: React.ReactNode, options: NotifyOptions = {}) {
  return toast.custom(
    (t: any) => (
      <ToastCard
        tone={tone}
        title={title}
        body={body}
        visible={t?.visible !== false}
        // App stamps `panther-light-toast` on <body> while the light toast theme is on; the
        // Toaster lives above every theme provider, so the class is the only signal here.
        isDark={!document.body.classList.contains('panther-light-toast')}
        onDismiss={() => toast.dismiss(t?.id)}
      />
    ),
    { id: options.id, duration: options.duration ?? DURATION[tone] },
  );
}

export const notify = {
  success: (title: React.ReactNode, body?: React.ReactNode, options?: NotifyOptions) =>
    show('success', title, body, options),
  error: (title: React.ReactNode, body?: React.ReactNode, options?: NotifyOptions) =>
    show('error', title, body, options),
  info: (title: React.ReactNode, body?: React.ReactNode, options?: NotifyOptions) =>
    show('info', title, body, options),
  warning: (title: React.ReactNode, body?: React.ReactNode, options?: NotifyOptions) =>
    show('warning', title, body, options),
  /** Stays until the caller replaces it (same id) or dismisses it. */
  loading: (title: React.ReactNode, body?: React.ReactNode, options?: NotifyOptions) =>
    show('loading', title, body, options),
  dismiss: (id?: string) => toast.dismiss(id),
};
